import { useMutation } from '@tanstack/react-query'; 
import { useAuthContext } from './useAuthContext';
import { useStateContext } from '../context/StateContext';
import axios from 'axios';
import { useErrorBoundary } from 'react-error-boundary';
import { optimizeImage } from './imageOptimizer';


export const mutationCall = async (data,{route, user, onSuccess=()=>{}, onError=()=>{}, retryCount=0, maxRetries=2}) => {
    try {
        if(!route) throw Error('no route')
        if(!user) throw Error('no user')
        if(!user.token) throw Error('no token')
        
        // Test error after validation but before axios call
        //throw new Error('test error')

        const response = await axios.post(route, {
            data 
        }, {
            withCredentials: true, 
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user.token}`,
                'X-Retry-Count': retryCount.toString() // Add retry count to headers
            },
            timeout: 50000, // 50 second timeout (leaving buffer for Vercel's 60s limit)
            timeoutErrorMessage: 'Request took too long to complete. Please try again.'
        });
        
        const responseData = response.data   
        if(responseData){
            // Clear any stored retry information for this route
            if (typeof window !== 'undefined' && window.sessionStorage) {
                try {
                    if (sessionStorage.getItem('lastTimeoutRoute') === route) {
                        sessionStorage.removeItem('lastTimeoutRoute');
                        sessionStorage.removeItem('lastTimeoutTime');
                        sessionStorage.removeItem('timeoutRetryCount');
                    }
                } catch (storageError) {
                    console.error('Error clearing timeout information:', storageError);
                }
            }
            
            onSuccess(responseData)
            return responseData
        }
    } catch(e) {
        console.log(`${route} catch error (attempt ${retryCount + 1}/${maxRetries + 1})`, e)
        
        // Enhanced error handling with better categorization
        let errorMessage = e?.message;
        let errorType = 'unknown';
        let shouldRetry = false;
        
        // Network and timeout errors
        if (e?.code === 'ECONNABORTED') {
            errorMessage = 'Request timed out. Please try again.';
            errorType = 'timeout';
            shouldRetry = true;
        } else if (e?.response?.status === 504) {
            errorMessage = 'Gateway Timeout: The server took too long to respond. Your data might still be processing. Please wait a moment and check if your changes were saved before trying again.';
            errorType = 'timeout';
            shouldRetry = true;
            
            // Log detailed information about the Gateway Timeout
            console.warn('Gateway Timeout (504) detected. This may be a temporary issue with the server or network.');
            
            // Store information about the failed request in sessionStorage
            if (typeof window !== 'undefined') {
                try {
                    const timeoutInfo = {
                        route,
                        timestamp: new Date().toISOString(),
                        retryCount: (parseInt(sessionStorage.getItem('timeoutRetryCount') || '0') + 1)
                    };
                    sessionStorage.setItem('lastTimeoutRoute', route);
                    sessionStorage.setItem('lastTimeoutTime', timeoutInfo.timestamp);
                    sessionStorage.setItem('timeoutRetryCount', timeoutInfo.retryCount.toString());
                    console.log('Timeout information stored:', timeoutInfo);
                } catch (storageError) {
                    console.error('Error storing timeout information:', storageError);
                }
            }
        } else if (e?.message === 'Network Error') {
            errorMessage = 'Network connection error. Please check your internet connection and try again.';
            errorType = 'network';
            shouldRetry = true;
        }
        
        // Authentication errors
        else if (e?.response?.status === 401) {
            errorMessage = 'Authentication failed. Please log in again.';
            errorType = 'auth';
            shouldRetry = false; // Don't retry auth errors
            
            // Clear token if it's invalid - this will trigger a re-login
            if (typeof window !== 'undefined') {
                localStorage.removeItem('token');
            }
        }
        
        // CORS errors - these are harder to detect directly
        else if (!e?.response && e?.message === 'Network Error') {
            // This is often a CORS error
            errorMessage = 'Cross-origin request failed. This might be a temporary issue. Try clearing your browser cache or reloading the page.';
            errorType = 'cors';
            shouldRetry = true;
            
            console.error('Possible CORS error:', e);
            
            // Suggest clearing localforage if this is a repeated CORS error
            if (typeof window !== 'undefined' && window.localforage) {
                console.log('Consider clearing localforage data if this error persists.');
            }
        }
        
        // Implement client-side retry logic
        if (shouldRetry && retryCount < maxRetries) {
            console.log(`Retrying request (${retryCount + 1}/${maxRetries})...`);
            
            // Add exponential backoff
            const backoffTime = Math.min(1000 * Math.pow(2, retryCount), 5000);
            console.log(`Waiting ${backoffTime}ms before retry...`);
            
            // Wait for backoff time
            await new Promise(resolve => setTimeout(resolve, backoffTime));
            
            // Retry the request with incremented retry count
            return mutationCall(data, {
                route, 
                user, 
                onSuccess, 
                onError, 
                retryCount: retryCount + 1, 
                maxRetries
            });
        }
        
        // If we've reached max retries or shouldn't retry, show error to user
        if (retryCount >= maxRetries) {
            console.log(`All ${maxRetries + 1} attempts failed. Showing error to user.`);
            
            // Add information about retry attempts to error message
            errorMessage = `${errorMessage} (After ${maxRetries + 1} attempts)`;
        }
        
        // Only show error to user if we've reached max retries or shouldn't retry
        onError({ ...e, message: errorMessage, errorType, retryCount, maxRetries })
        throw e // Propagate error to component's onError handler
    }
} 

export const fileMutationCall = async (data, {route, user, onSuccess=()=>{}, onError=()=>{}, onProgress=()=>{}, retryCount=0, maxRetries=2}) => {
    try {
        if(!route) throw Error('no route')
        if(!user) throw Error('no user')
        if(!user.token) throw Error('no token')

        const formData = new FormData()
        
        // Handle file data specifically
        if (data.file) {
            let fileToUpload = data.file;
            
            // If it's an image, optimize it before upload
            if (data.file.type.startsWith('image/')) {
                fileToUpload = await optimizeImage(data.file);
            }
            
            formData.append('file', fileToUpload)
            // Remove file from data object before appending rest of data
            const { file, ...restData } = data
            formData.append('data', JSON.stringify(restData))
        } else {
            formData.append('data', JSON.stringify(data))
        }

        const response = await axios.post(route, formData, {
            withCredentials: true,
            headers: {
                'Authorization': `Bearer ${user.token}`,
                'X-Retry-Count': retryCount.toString(), // Add retry count to headers
                // Don't set Content-Type here, let axios set it with boundary for FormData
            },
            timeout: 30000, // 30 second timeout should be plenty after optimization
            timeoutErrorMessage: 'File upload took too long to complete. Please try again.',
            onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted);
            }
        });

        const responseData = response.data
        if(responseData){
            // Clear any stored retry information for this route
            if (typeof window !== 'undefined' && window.sessionStorage) {
                try {
                    if (sessionStorage.getItem('lastFileTimeoutRoute') === route) {
                        sessionStorage.removeItem('lastFileTimeoutRoute');
                        sessionStorage.removeItem('lastFileTimeoutTime');
                        sessionStorage.removeItem('fileTimeoutRetryCount');
                    }
                } catch (storageError) {
                    console.error('Error clearing file timeout information:', storageError);
                }
            }
            
            onSuccess(responseData)
            return responseData
        }
    } catch(e) {
        console.log(`${route} catch error (attempt ${retryCount + 1}/${maxRetries + 1})`, e)
        
        // Enhanced error handling with better categorization
        let errorMessage = e?.message;
        let errorType = 'unknown';
        
        // Network and timeout errors
        let shouldRetry = false;
        
        if (e?.code === 'ECONNABORTED') {
            errorMessage = 'File upload timed out. Please try again.';
            errorType = 'timeout';
            shouldRetry = true;
        } else if (e?.response?.status === 504) {
            errorMessage = 'Gateway Timeout: The server took too long to respond. Your file might still be processing. Please wait a moment and check if your upload was completed before trying again.';
            errorType = 'timeout';
            shouldRetry = true;
            
            // Log detailed information about the Gateway Timeout
            console.warn('Gateway Timeout (504) detected during file upload. This may be a temporary issue with the server or network.');
            
            // Store information about the failed request in sessionStorage
            if (typeof window !== 'undefined') {
                try {
                    const timeoutInfo = {
                        route,
                        timestamp: new Date().toISOString(),
                        retryCount: (parseInt(sessionStorage.getItem('fileTimeoutRetryCount') || '0') + 1)
                    };
                    sessionStorage.setItem('lastFileTimeoutRoute', route);
                    sessionStorage.setItem('lastFileTimeoutTime', timeoutInfo.timestamp);
                    sessionStorage.setItem('fileTimeoutRetryCount', timeoutInfo.retryCount.toString());
                    console.log('File timeout information stored:', timeoutInfo);
                } catch (storageError) {
                    console.error('Error storing file timeout information:', storageError);
                }
            }
        } else if (e?.message === 'Network Error') {
            errorMessage = 'Network connection error. Please check your internet connection and try again.';
            errorType = 'network';
            shouldRetry = true;
        }
        
        // Authentication errors
        else if (e?.response?.status === 401) {
            errorMessage = 'Authentication failed. Please log in again.';
            errorType = 'auth';
            shouldRetry = false; // Don't retry auth errors
            
            // Clear token if it's invalid - this will trigger a re-login
            if (typeof window !== 'undefined') {
                localStorage.removeItem('token');
            }
        }
        
        // File-specific errors
        else if (e?.response?.status === 413) {
            errorMessage = 'File is too large. Please try a smaller file.';
            errorType = 'file_size';
            shouldRetry = false; // Don't retry file size errors
        }
        
        // CORS errors - these are harder to detect directly
        else if (!e?.response && e?.message === 'Network Error') {
            // This is often a CORS error
            errorMessage = 'Cross-origin request failed. This might be a temporary issue. Try clearing your browser cache or reloading the page.';
            errorType = 'cors';
            shouldRetry = true;
            
            console.error('Possible CORS error:', e);
            
            // Suggest clearing localforage if this is a repeated CORS error
            if (typeof window !== 'undefined' && window.localforage) {
                console.log('Consider clearing localforage data if this error persists.');
            }
        }
        
        // Implement client-side retry logic
        if (shouldRetry && retryCount < maxRetries) {
            console.log(`Retrying file upload (${retryCount + 1}/${maxRetries})...`);
            
            // Add exponential backoff
            const backoffTime = Math.min(1000 * Math.pow(2, retryCount), 5000);
            console.log(`Waiting ${backoffTime}ms before retry...`);
            
            // Wait for backoff time
            await new Promise(resolve => setTimeout(resolve, backoffTime));
            
            // Retry the request with incremented retry count
            return fileMutationCall(data, {
                route, 
                user, 
                onSuccess, 
                onError, 
                onProgress,
                retryCount: retryCount + 1, 
                maxRetries
            });
        }
        
        // If we've reached max retries or shouldn't retry, show error to user
        if (retryCount >= maxRetries) {
            console.log(`All ${maxRetries + 1} file upload attempts failed. Showing error to user.`);
            
            // Add information about retry attempts to error message
            errorMessage = `${errorMessage} (After ${maxRetries + 1} attempts)`;
        }
        
        // Only show error to user if we've reached max retries or shouldn't retry
        onError({ ...e, message: errorMessage, errorType, retryCount, maxRetries })
        throw e // Propagate error to component's onError handler
    }
}

//===============================
export const useCreateReplace = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/create-replace`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
} 

export const useUploadPhoto = ({onProgress, ...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation(
        (data) => fileMutationCall(data, {
            route: `${apiUrl}/calls/add-file`,
            user,
            onError: (e) => showBoundary(new Error(e.message)),
            onProgress: onProgress || (() => {})
        }),
        {...props}
    )
}

export const useUploadFile = ({onProgress, ...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation(
        (data) => fileMutationCall(data, {
            route: `${apiUrl}/calls/add-file`,
            user,
            onError: (e) => showBoundary(new Error(e.message)),
            onProgress: onProgress || (() => {})
        }),
        {...props}
    )
}

export const useDeletePhoto = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>fileMutationCall(data, {route: `${apiUrl}/calls/delete-file`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const useDeleteDocument = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/delete`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const useAppend = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/append`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const usePrepend = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/prepend`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const useSet = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/set-at-index`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const useUnset = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/unset`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const useReplaceInArray = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/replace-in-array`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const useDeleteInArray = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/delete-in-array`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const useReplaceDevice = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/replace-device`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const useReplaceOffer = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/replace-offer`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}

export const useReplacePriceList = ({...props}) => {
    const { showBoundary } = useErrorBoundary();
    const {user} = useAuthContext()
    const {apiUrl} = useStateContext()
    return useMutation((data)=>mutationCall(data, {route: `${apiUrl}/calls/replace-price-list`, user, onError:(e)=>{showBoundary(new Error(e.message))}}),{...props})
}
